import React from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  FileText, Wallet, QrCode, Send, Save, CheckCircle2, TrendingUp,
  Pencil, ShieldCheck, X, Download, Star, ArrowRight, Users,
  Plane, Calculator, Camera, UserCheck, Plus, Minus, HelpCircle, Lock, Car, Receipt, Globe, BookOpen,
} from "lucide-react";
import { Button } from "./Button";
import CrmSection from "./CrmSection";
import WorkforceSection from "./WorkforceSection";
import ExpenseSection from "./ExpenseSection";
import CommerceSection from "./CommerceSection";
import { landingContent } from "./i18n";

// ===== Ablefy-Zahlungslinks =====
const ABO_149_URL = "https://myablefy.com/s/femmesmarketingstudio/angebots-rechnungsstudio-angebote-schreiben-rechnungen-stellen-schneller-bezahlt-werden-a98a05cf/payment";
const ABO_89_URL = "https://myablefy.com/s/femmesmarketingstudio/angebots-rechnungsstudio-angebote-schreiben-rechnungen-stellen-schneller-bezahlt-werden-3d26c7c7/payment";
const ABO_39_URL = "https://myablefy.com/s/femmesmarketingstudio/angebots-rechnungsstudio-75ed5a6e/payment";

const REISE_CHECKOUT = { basic: "https://myablefy.com/s/femmesmarketingstudio/business-travel-studio-basic-771fc10c/payment", business: "https://myablefy.com/s/femmesmarketingstudio/business-travel-studio-business-7c650c44/payment", premium: "https://myablefy.com/s/femmesmarketingstudio/business-travel-studio-premium-d163d95e/payment", kauf: "https://myablefy.com/s/femmesmarketingstudio/dein-eigenes-business-travel-studio-dauerhaft-einmal-kaufen-und-dauerhaft-besitzen-inkl-white-label-option-unter-deiner-eigenen-marke-3ef4d8d4/payment" };

const WL_CHECKOUT = {
  rechnung: "https://myablefy.com/s/femmesmarketingstudio/white-label-lizenz-deine-marke-deine-software-dein-umsatz-eigenes-saas-business-b698b640/payment",
  crm: "https://myablefy.com/s/femmesmarketingstudio/crm-studio-white-label-beinhaltet-komplettes-crm-studio-im-premium-funktionsumfang-unter-eigener-marke-49b2a2e7/payment",
  travel: "https://myablefy.com/s/femmesmarketingstudio/dein-eigenes-business-travel-studio-dauerhaft-einmal-kaufen-und-dauerhaft-besitzen-inkl-white-label-option-unter-deiner-eigenen-marke-3ef4d8d4/payment",
  workforce: "https://myablefy.com/s/femmesmarketingstudio/white-label-lizenz-deine-marke-deine-software-dein-umsatz-eigenes-saas-project-workforce-studio-d596f9da/payment",
  expense: "https://myablefy.com/s/femmesmarketingstudio/kopie-von-white-label-lizenz-deine-marke-deine-software-dein-umsatz-eigenes-saas-project-workforce-studio-ed489d1a/payment",
  commerce: "https://myablefy.com/s/femmesmarketingstudio/kopie-von-white-label-lizenz-deine-marke-deine-software-dein-umsatz-eigenes-saas-business-travel-studio-7c564b32/payment",
  komplett: "https://myablefy.com/s/femmesmarketingstudio/kopie-von-vier-lizenzen-white-label-lizenz-deine-marke-deine-software-dein-umsatz-15dcd49b/payment",
};

// ===== Sprachunabhängige Metadaten (Preise, Icons, Struktur) =====
const FEATURE_ICONS = [Users, FileText, Wallet, QrCode, Send, Save, CheckCircle2, TrendingUp, Pencil, ShieldCheck, Receipt];
const REISE_FEATURE_ICONS = [Plane, Calculator, Camera, UserCheck, Car];

const LANDING_PLAN_META = [
  { price: "29", checkout: ABO_39_URL, highlight: false },
  { price: "49", checkout: ABO_89_URL, highlight: true },
  { price: "99", checkout: ABO_149_URL, highlight: false },
];

const REISE_PLAN_META = { basic: { icon: Plane, price: "49", highlight: false }, business: { icon: Calculator, price: "89", highlight: true }, premium: { icon: UserCheck, price: "129", highlight: false } };

// Vergleichs-Matrix (s = Starter, p = Pro, b = Business) – Reihenfolge = compareRows in i18n
const COMPARE_MATRIX = [
  { s: true, p: true, b: true },
  { s: true, p: true, b: true },
  { s: true, p: true, b: true },
  { s: true, p: true, b: true },
  { s: true, p: true, b: true },
  { s: false, p: true, b: true },
  { s: false, p: true, b: true },
  { s: false, p: true, b: true },
  { s: false, p: true, b: true },
  { s: false, p: true, b: true },
  { s: false, p: true, b: true },
  { s: false, p: true, b: true },
  { s: false, p: false, b: true },
  { s: false, p: false, b: true },
  { s: false, p: true, b: true },
  { s: "1", p: "1", b: "multi" },
];

const WL_CARD_META = [
  { key: "rechnung", p: "12.000 €" },
  { key: "crm", p: "15.000 €" },
  { key: "travel", p: "15.000 €" },
  { key: "workforce", p: "15.000 €" },
  { key: "expense", p: "12.000 €" },
  { key: "commerce", p: "15.000 €" },
  { key: "komplett", p: "74.900 €", old: "84.000 €", hl: true },
];

const SAMPLE_GIRO_PAYLOAD = [
  "BCD", "002", "1", "SCT", "COBADEFFXXX",
  "Muster GmbH", "DE89370400440532013000", "EUR1190.00", "", "",
  "Rechnung RE-2026-001",
].join("\n");

const SAMPLE_SWISS_PAYLOAD = [
  "SPC", "0200", "1", "CH9300762011623852957",
  "K", "Muster GmbH", "Musterstraße 12", "10115 Berlin", "", "", "CH",
  "", "", "", "", "", "", "",
  "2702.50", "CHF",
  "K", "Schweizer Kunde AG", "Bahnhofstr. 3", "8001 Zürich", "", "", "CH",
  "NON", "",
  "Rechnung RE-2026-002",
  "EPD",
].join("\n");

const CompareCell = ({ v }) => {
  if (v === true) return <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 inline-block" aria-hidden="true" />;
  if (v === false) return <X className="w-4 h-4 sm:w-5 sm:h-5 text-red-500 inline-block" aria-hidden="true" />;
  return <span className="text-[11px] sm:text-xs font-semibold text-[var(--brand-ink)]">{v}</span>;
};

const FaqSection = ({ t }) => {
  const [open, setOpen] = React.useState(0);
  return (
    <section className="px-6 lg:px-12 py-20 bg-white" data-testid="landing-faq">
      <div className="max-w-3xl mx-auto">
        <span className="flex items-center justify-center gap-2 text-xs uppercase tracking-[0.2em] text-[var(--brand-primary)] font-semibold mb-3">
          <HelpCircle className="w-4 h-4" /> {t.eyebrow}
        </span>
        <h2 className="font-[Playfair_Display] text-3xl font-bold text-center text-[var(--brand-ink)] mb-3">{t.heading}</h2>
        <p className="text-center text-[var(--brand-muted)] mb-10 text-sm">{t.sub}</p>
        <div className="space-y-3">
          {t.items.map((item, i) => {
            const isOpen = open === i;
            return (
              <div key={i} className="rounded-2xl border border-[var(--brand-border)] bg-white overflow-hidden transition-shadow hover:shadow-sm" data-testid={`faq-item-${i}`}>
                <button onClick={() => setOpen(isOpen ? -1 : i)} className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left" data-testid={`faq-question-${i}`} aria-expanded={isOpen}>
                  <span className="font-semibold text-[var(--brand-ink)]">{item.q}</span>
                  <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${isOpen ? "bg-[var(--brand-primary)] text-white" : "bg-[var(--brand-border)]/50 text-[var(--brand-ink)]"}`}>
                    {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                  </span>
                </button>
                <div className={`grid transition-all duration-300 ease-in-out ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                  <div className="overflow-hidden">
                    <p className="px-6 pb-5 text-sm leading-relaxed text-[var(--brand-muted)]" data-testid={`faq-answer-${i}`}>{item.a}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const LandingPage = ({ onLogin, onWhiteLabel, onBusinessCheck, onImpressum, onDatenschutz, onAgb, lang = "de", setLang }) => {
  const t = landingContent[lang];
  const demoFrom = () => { const el = document.getElementById("video"); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); };
  const toggleLang = () => setLang && setLang(lang === "de" ? "en" : "de");
  const API = process.env.REACT_APP_BACKEND_URL;
  const openPdf = (file) => window.open(API ? `${API}/api/pdf/${file}` : `/pdf/${file}`, "_blank", "noopener,noreferrer");

  return (
    <div className="min-h-screen bg-white overflow-x-hidden" data-testid="landing-page">
      <nav className="flex items-center justify-between gap-2 px-4 sm:px-6 lg:px-12 py-5 max-w-6xl mx-auto">
        <div className="leading-tight min-w-0">
          <div className="font-[Playfair_Display] text-base sm:text-lg lg:text-xl font-bold text-[var(--brand-ink)]">{t.nav.brand}</div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--brand-muted)]">{t.nav.tagline}</div>
        </div>
        <button onClick={toggleLang} className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-full border border-[var(--brand-border)] text-[var(--brand-ink)] hover:bg-[var(--brand-border-light)] transition-colors px-3.5 py-1.5 text-xs font-semibold" data-testid="lang-toggle-btn" aria-label={`Switch to ${t.switchTo}`}>
          <Globe className="w-3.5 h-3.5" />
          <span className={lang === "de" ? "text-[var(--brand-primary)]" : ""}>DE</span>
          <span className="text-[var(--brand-muted)]">/</span>
          <span className={lang === "en" ? "text-[var(--brand-primary)]" : ""}>EN</span>
        </button>
      </nav>

      <header className="px-6 lg:px-12 py-16 lg:py-24" style={{ background: "linear-gradient(160deg, #3A4148 0%, #2E3439 100%)" }}>
        <div className="max-w-4xl mx-auto text-center text-white">
          <span className="inline-block text-xs uppercase tracking-[0.2em] text-white/60 mb-5">{t.hero.eyebrow}</span>
          <h1 className="font-[Playfair_Display] text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">{t.hero.title[0]}<br />{t.hero.title[1]}<br />{t.hero.title[2]}</h1>
          <p className="text-base md:text-lg text-white font-semibold max-w-2xl mx-auto mb-3">{t.hero.lead}</p>
          <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto mb-4">{t.hero.sub}</p>
          <p className="text-sm text-white/60 max-w-2xl mx-auto mb-5">{t.hero.note}</p>
          <div className="flex flex-wrap items-center justify-center gap-2 mb-9" data-testid="hero-modules">
            <span className="text-xs text-white/50 mr-1">{t.hero.modulesLabel}</span>
            {t.hero.modules.map((m) => (
              <span key={m} className="text-xs font-semibold text-white/90 bg-white/10 border border-white/15 rounded-full px-3 py-1">{m}</span>
            ))}
          </div>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button onClick={demoFrom} className="bg-white text-[var(--brand-ink)] hover:bg-white/90 rounded-full px-8 py-3 font-semibold" data-testid="landing-demo-btn">{t.hero.ctaDemo}</Button>
            <Button onClick={demoFrom} variant="outline" className="border border-white/40 text-white hover:bg-white/10 rounded-full px-8 py-3 font-semibold bg-transparent" data-testid="landing-demo-access-btn">{t.hero.ctaDemoAccess}</Button>
            <Button onClick={() => openPdf("StudioSuite_Gesamt.pdf")} variant="outline" className="border border-white/40 text-white hover:bg-white/10 rounded-full px-8 py-3 font-semibold bg-transparent" data-testid="landing-pdf-btn"><Download className="w-4 h-4 mr-2 inline-block" /> {t.hero.ctaPdf}</Button>
            <Button onClick={onBusinessCheck} variant="outline" className="border border-white/40 text-white hover:bg-white/10 rounded-full px-8 py-3 font-semibold bg-transparent" data-testid="landing-check-btn">{t.hero.ctaCheck || "Finde dein Business-Setup"}</Button>
          </div>
          <p className="text-xs text-white/50 mt-4">{t.hero.note2}</p>
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 mt-7 text-xs sm:text-sm text-white/70" data-testid="landing-trust-badges">
            {t.hero.trustBadges.map((b, i) => (
              <span key={b} className="flex items-center gap-3">
                {i > 0 && <span className="text-white/25">•</span>}
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-white/80" /> {b}</span>
              </span>
            ))}
          </div>
        </div>
      </header>

      <section id="funktionen" className="px-6 lg:px-12 py-16 max-w-6xl mx-auto scroll-mt-24">
        <h2 className="font-[Playfair_Display] text-3xl font-bold text-center text-[var(--brand-ink)] mb-3">{t.features.heading}</h2>
        <p className="text-center text-[var(--brand-muted)] mb-12 max-w-xl mx-auto">{t.features.sub}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {t.features.items.map((f, i) => {
            const Icon = FEATURE_ICONS[i] || FileText;
            return (
              <div key={f.title} className="p-5 rounded-xl border border-[var(--brand-border)] hover:shadow-md transition-shadow">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ background: "var(--brand-border-light)" }}>
                  <Icon className="w-5 h-5 text-[var(--brand-primary)]" />
                </div>
                <h3 className="font-semibold text-[var(--brand-ink)] mb-1">{f.title}</h3>
                <p className="text-sm text-[var(--brand-muted)]">{f.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="px-6 lg:px-12 py-16 max-w-6xl mx-auto" data-testid="landing-qr-showcase">
        <h2 className="font-[Playfair_Display] text-3xl font-bold text-center text-[var(--brand-ink)] mb-3">{t.qr.heading}</h2>
        <p className="text-center text-[var(--brand-muted)] mb-10 max-w-2xl mx-auto">{t.qr.sub}</p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div>
            <div className="text-[11px] uppercase tracking-wider font-semibold text-[var(--brand-primary)] mb-3 text-center lg:text-left">{t.qr.custViewLabel}</div>
            <div className="rounded-xl overflow-hidden border border-[var(--brand-border)] shadow-xl bg-white" data-testid="invoice-mockup">
              <div className="flex items-center gap-2 px-4 py-2.5 bg-[var(--brand-border-light)] border-b border-[var(--brand-border)]">
                <span className="w-3 h-3 rounded-full bg-red-400" />
                <span className="w-3 h-3 rounded-full bg-amber-400" />
                <span className="w-3 h-3 rounded-full bg-green-400" />
                <span className="ml-3 flex-1 text-[11px] text-[var(--brand-muted)] bg-white rounded-full px-3 py-1 border border-[var(--brand-border)] truncate">🔒 deine-firma.de/rechnung/RE-2026-001</span>
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="w-9 h-9 rounded-lg bg-[var(--brand-ink)] text-white flex items-center justify-center font-bold mb-2">DF</div>
                    <div className="font-semibold text-[var(--brand-ink)] text-sm">Deine Firma GmbH</div>
                    <div className="text-[11px] text-[var(--brand-muted)]">Musterstraße 12 · 10115 Berlin</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-[var(--brand-muted)]">{t.qr.invoiceLabel}</div>
                    <div className="font-mono text-sm font-semibold text-[var(--brand-ink)]">RE-2026-001</div>
                    <div className="text-[11px] text-[var(--brand-muted)]">30.06.2026</div>
                  </div>
                </div>
                <div className="border-t border-[var(--brand-border)]">
                  <div className="flex justify-between py-2 text-xs border-b border-[var(--brand-border-light)]">
                    <span className="text-[var(--brand-ink)]">{t.qr.lineItem}</span>
                    <span className="font-medium">1.000,00 €</span>
                  </div>
                  <div className="flex justify-between py-2 text-xs border-b border-[var(--brand-border-light)]">
                    <span className="text-[var(--brand-muted)]">{t.qr.vatLine}</span>
                    <span className="text-[var(--brand-muted)]">190,00 €</span>
                  </div>
                </div>
                <div className="flex justify-between items-center px-3 py-2.5 mt-2 rounded-lg bg-[var(--brand-ink)] text-white">
                  <span className="text-sm font-semibold">{t.qr.total}</span>
                  <span className="font-[Playfair_Display] text-lg font-bold">1.190,00 €</span>
                </div>
                <div className="flex items-center gap-3 mt-4 p-3 rounded-lg border border-dashed border-[var(--brand-border)]">
                  <QRCodeSVG value={SAMPLE_GIRO_PAYLOAD} size={64} level="M" />
                  <div className="text-[11px] text-[var(--brand-muted)] leading-snug">
                    <span className="font-semibold text-[var(--brand-ink)]">{t.qr.payBankApp}</span><br />
                    {t.qr.scanNote}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="space-y-6">
            <div className="rounded-2xl border border-[var(--brand-border)] bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <span className="text-[11px] uppercase tracking-wider font-semibold text-[var(--brand-primary)]">Deutschland · GiroCode</span>
                <span className="text-xs text-[var(--brand-muted)]">{t.qr.sepa}</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="shrink-0" style={{ width: 110, height: 110 }}>
                  <QRCodeSVG value={SAMPLE_GIRO_PAYLOAD} size={110} level="M" />
                </div>
                <div className="text-sm space-y-1">
                  <div className="text-[var(--brand-muted)] text-xs">{t.qr.scanInApp}</div>
                  <div><span className="text-[var(--brand-muted)]">{t.qr.amount}</span><span className="font-semibold">1.190,00 €</span></div>
                  <div className="font-mono text-[11px] break-all">DE89 3704 0044 0532 0130 00</div>
                </div>
              </div>
            </div>
            <div className="rounded-2xl border border-[var(--brand-border)] bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <span className="text-[11px] uppercase tracking-wider font-semibold text-[var(--brand-primary)]">Schweiz · Swiss QR</span>
                <span className="text-xs text-[var(--brand-muted)]">{t.qr.qrRech}</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="relative shrink-0" style={{ width: 110, height: 110 }}>
                  <QRCodeSVG value={SAMPLE_SWISS_PAYLOAD} size={110} level="M" />
                  <div className="absolute top-1/2 left-1/2" style={{ transform: "translate(-50%, -50%)", width: 24, height: 24, background: "#000", border: "2px solid #fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <div style={{ position: "relative", width: 14, height: 14 }}>
                      <div style={{ position: "absolute", top: 5, left: 1, width: 12, height: 4, background: "#fff" }} />
                      <div style={{ position: "absolute", top: 1, left: 5, width: 4, height: 12, background: "#fff" }} />
                    </div>
                  </div>
                </div>
                <div className="text-sm space-y-1">
                  <div className="text-[var(--brand-muted)] text-xs">{t.qr.payableEbanking}</div>
                  <div><span className="text-[var(--brand-muted)]">{t.qr.amount}</span><span className="font-semibold">CHF 2'702.50</span></div>
                  <div className="font-mono text-[11px] break-all">CH93 0076 2011 6238 5295 7</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="text-center mt-10">
          <Button onClick={demoFrom} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white rounded-full px-10 py-4 text-base font-semibold shadow-md" data-testid="qr-showcase-demo-btn">{t.qr.ctaDemo}</Button>
          <p className="text-sm text-[var(--brand-muted)] mt-4">{t.qr.demoNote}</p>
        </div>
        <p className="text-center text-xs text-[var(--brand-muted)] mt-6 max-w-2xl mx-auto">{t.qr.demoNote2}</p>
      </section>

      <section id="video" className="px-6 lg:px-12 py-16 scroll-mt-16" style={{ background: "var(--brand-border-light)" }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="font-[Playfair_Display] text-3xl font-bold text-center text-[var(--brand-ink)] mb-3">{t.steps.heading}</h2>
          <p className="text-center text-[var(--brand-muted)] mb-10 max-w-2xl mx-auto">{t.steps.sub}</p>
          <div className="max-w-3xl mx-auto mb-14">
            <div className="text-center mb-4">
              <span className="inline-block text-xs uppercase tracking-[0.2em] text-[var(--brand-primary)] font-semibold" data-testid="video-eyebrow">{t.steps.videoEyebrow}</span>
            </div>
            <div className="rounded-2xl overflow-hidden border border-[var(--brand-border)] shadow-xl bg-black" data-testid="landing-intro-video">
              <video controls playsInline preload="metadata" poster="/video-poster.jpg" className="w-full aspect-video block object-cover bg-black" data-testid="studio-suite-video">
                <source src="/business_studio_suite_prozessdemo.mp4" type="video/mp4" />
                {t.steps.videoFallback} <a href="/business_studio_suite_prozessdemo.mp4" className="underline">{t.steps.videoDownload}</a>
              </video>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {t.steps.items.map((s, i) => (
              <div key={s.title} className="text-center">
                <div className="w-12 h-12 rounded-full bg-[var(--brand-primary)] text-white font-bold text-lg flex items-center justify-center mx-auto mb-4">{i + 1}</div>
                <h3 className="font-semibold text-[var(--brand-ink)] mb-2">{s.title}</h3>
                <p className="text-sm text-[var(--brand-muted)]">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 lg:px-12 py-16 max-w-6xl mx-auto" data-testid="landing-pricing">
        <div className="text-center mb-2"><span className="inline-block text-xs uppercase tracking-[0.2em] text-[var(--brand-primary)] font-semibold">{t.pricing.eyebrow}</span></div>
        <h2 className="font-[Playfair_Display] text-3xl font-bold text-center text-[var(--brand-ink)] mb-3">{t.pricing.heading}</h2>
        <p className="text-center text-[var(--brand-muted)] mb-6 max-w-2xl mx-auto">{t.pricing.sub}</p>

        <div className="flex flex-wrap justify-center gap-3 mb-10">
          <Button onClick={() => openPdf("Angebots_Rechnungsstudio.pdf")} variant="outline" className="border border-[var(--brand-border)] text-[var(--brand-ink)] hover:bg-[var(--brand-border-light)] rounded-full px-7 py-2.5 font-semibold bg-transparent" data-testid="rechnung-pdf-btn"><Download className="w-4 h-4 mr-2 inline-block" /> {t.pricing.pdfBtn}</Button>
          <Button onClick={() => openPdf("Anleitung_Angebots_Rechnungsstudio.pdf")} variant="outline" className="border border-[var(--brand-border)] text-[var(--brand-ink)] hover:bg-[var(--brand-border-light)] rounded-full px-7 py-2.5 font-semibold bg-transparent" data-testid="rechnung-guide-btn"><BookOpen className="w-4 h-4 mr-2 inline-block" /> {t.guideBtn}</Button>
        </div>

        <div className="text-center mb-8">
          <span className="inline-block text-xs uppercase tracking-[0.2em] text-[var(--brand-primary)] font-semibold">{t.pricing.rentEyebrow}</span>
          <p className="text-sm text-[var(--brand-muted)] mt-1">{t.pricing.rentNote}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          {t.pricing.plans.map((p, i) => {
            const m = LANDING_PLAN_META[i];
            return (
              <div key={p.name} className={`p-6 rounded-2xl border ${m.highlight ? "border-[var(--brand-primary)] shadow-lg relative" : "border-[var(--brand-border)]"}`} data-testid={`plan-${p.name.toLowerCase()}`}>
                {m.highlight && (<span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[var(--brand-primary)] text-white text-xs px-3 py-1 rounded-full">{t.pricing.popular}</span>)}
                <h3 className="font-semibold text-lg text-[var(--brand-ink)]">{p.name}</h3>
                <div className="my-3"><span className="text-4xl font-bold text-[var(--brand-ink)]">{m.price} €</span><span className="text-[var(--brand-muted)]">{p.per}</span></div>
                <p className="text-xs text-[var(--brand-muted)] mb-3">{t.pricing.orYear(p.year)}</p>
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--brand-primary)] bg-[var(--brand-border-light)] border border-[var(--brand-primary)]/25 rounded-full px-3 py-1 mb-4">
                  <Star className="w-3.5 h-3.5 fill-[var(--brand-primary)]" /> {t.pricing.yearBonus}
                </span>
                {m.highlight && (
                  <p className="text-xs font-semibold text-[var(--brand-primary)] mb-3 flex items-center gap-1.5">
                    <Star className="w-3.5 h-3.5 fill-[var(--brand-primary)]" /> {t.pricing.popularChoice}
                  </p>
                )}
                <ul className="space-y-2 mb-6">
                  {p.feats.map((ft) => (<li key={ft} className="flex items-start gap-2 text-sm text-[var(--brand-ink)]"><CheckCircle2 className="w-4 h-4 text-[var(--brand-primary)] mt-0.5 shrink-0" /> {ft}</li>))}
                </ul>
                <Button onClick={() => (m.checkout ? window.open(m.checkout, "_blank", "noopener,noreferrer") : onLogin())} className={`w-full rounded-full ${m.highlight ? "bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white" : "bg-[var(--brand-ink)] hover:bg-black text-white"}`} data-testid={`plan-buy-${p.name.toLowerCase()}`}>{m.checkout ? t.pricing.buyNow : t.pricing.startNow}</Button>
              </div>
            );
          })}
        </div>

        <div className="mt-16" data-testid="landing-compare">
          <h3 className="font-[Playfair_Display] text-2xl sm:text-3xl font-bold text-center text-[var(--brand-ink)] mb-2">{t.pricing.compareHeading}</h3>
          <p className="text-center text-sm text-[var(--brand-muted)] mb-8">{t.pricing.compareSub}</p>
          <div className="overflow-x-auto rounded-2xl border border-[var(--brand-border)]">
            <table className="w-full text-xs sm:text-sm">
              <thead>
                <tr className="border-b border-[var(--brand-border)] bg-[var(--brand-border-light)]">
                  <th className="text-left font-semibold text-[var(--brand-ink)] px-3 sm:px-5 py-3 sm:py-4">{t.pricing.colFeature}</th>
                  <th className="text-center font-semibold text-[var(--brand-ink)] px-1.5 sm:px-4 py-3 sm:py-4 w-14 sm:w-28">Starter</th>
                  <th className="text-center font-semibold text-[var(--brand-ink)] px-1.5 sm:px-4 py-3 sm:py-4 w-14 sm:w-28">Pro</th>
                  <th className="text-center font-semibold text-white px-1.5 sm:px-4 py-3 sm:py-4 w-14 sm:w-28 rounded-t-lg" style={{ background: "var(--brand-primary)" }}>Business</th>
                </tr>
              </thead>
              <tbody>
                {t.pricing.compareRows.map((label, idx) => {
                  const row = COMPARE_MATRIX[idx];
                  const val = (v) => (v === "multi" ? t.pricing.multiUsers : v);
                  return (
                    <tr key={label} className={idx % 2 ? "bg-[var(--brand-border-light)]/50" : ""}>
                      <td className="text-left text-[var(--brand-ink)] px-3 sm:px-5 py-3 sm:py-3.5">{label}</td>
                      <td className="text-center px-1.5 sm:px-4 py-3 sm:py-3.5"><CompareCell v={val(row.s)} /></td>
                      <td className="text-center px-1.5 sm:px-4 py-3 sm:py-3.5"><CompareCell v={val(row.p)} /></td>
                      <td className="text-center px-1.5 sm:px-4 py-3 sm:py-3.5" style={{ background: "rgba(44,51,56,0.06)" }}><CompareCell v={val(row.b)} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-center text-xs text-[var(--brand-muted)] mt-4">{t.pricing.compareNote}</p>
        </div>

        <div id="whitelabel" className="mt-16 rounded-2xl overflow-hidden scroll-mt-24" data-testid="landing-whitelabel-teaser" style={{ background: "linear-gradient(160deg, #2C3338 0%, #1B1F23 100%)" }}>
          <div className="p-8 lg:p-12 text-white text-center">
            <span className="inline-block text-xs uppercase tracking-[0.2em] text-white/60 mb-3">{t.whitelabel.eyebrow}</span>
            <h3 className="font-[Playfair_Display] text-2xl lg:text-3xl font-bold mb-4">{t.whitelabel.heading}</h3>
            <p className="text-base text-white/80 mb-3 max-w-2xl mx-auto">{t.whitelabel.teaserText}</p>
            <p className="text-sm text-white/60 mb-8 max-w-2xl mx-auto">{t.whitelabel.teaserSub}</p>
            <Button onClick={onWhiteLabel} className="bg-white text-[var(--brand-ink)] hover:bg-white/90 rounded-full px-10 py-4 text-base font-semibold shadow-md" data-testid="whitelabel-teaser-link">{t.whitelabel.teaserLink}</Button>
          </div>
        </div>
      </section>

      <CrmSection onLogin={onLogin} lang={lang} />

      <section className="px-6 lg:px-12 py-16" data-testid="landing-reisestudio">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-2"><span className="inline-block text-xs uppercase tracking-[0.2em] text-[var(--brand-primary)] font-semibold">{t.reise.eyebrow}</span></div>
          <h2 className="font-[Playfair_Display] text-3xl font-bold text-center text-[var(--brand-ink)] mb-3">{t.reise.heading}</h2>
          <p className="text-center text-[var(--brand-muted)] mb-6 max-w-2xl mx-auto">{t.reise.sub}</p>
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            <Button onClick={() => openPdf("Business_Travel_Studio.pdf")} variant="outline" className="border border-[var(--brand-border)] text-[var(--brand-ink)] hover:bg-[var(--brand-border-light)] rounded-full px-7 py-2.5 font-semibold bg-transparent" data-testid="reise-pdf-btn"><Download className="w-4 h-4 mr-2 inline-block" /> {t.reise.pdfBtn}</Button>
            <Button onClick={() => openPdf("Anleitung_Business_Travel_Studio.pdf")} variant="outline" className="border border-[var(--brand-border)] text-[var(--brand-ink)] hover:bg-[var(--brand-border-light)] rounded-full px-7 py-2.5 font-semibold bg-transparent" data-testid="reise-guide-btn"><BookOpen className="w-4 h-4 mr-2 inline-block" /> {t.guideBtn}</Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.reise.features.map((f, i) => {
              const Icon = REISE_FEATURE_ICONS[i] || Plane;
              return (
                <div key={f.title} className="p-5 rounded-xl border border-[var(--brand-border)] hover:shadow-md transition-shadow">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ background: "var(--brand-border-light)" }}>
                    <Icon className="w-5 h-5 text-[var(--brand-primary)]" />
                  </div>
                  <h3 className="font-semibold text-[var(--brand-ink)] mb-1">{f.title}</h3>
                  <p className="text-sm text-[var(--brand-muted)]">{f.text}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-8 rounded-2xl overflow-hidden grid grid-cols-1 md:grid-cols-5" style={{ background: "linear-gradient(160deg, #2C3338 0%, #1B1F23 100%)" }} data-testid="reisestudio-sync">
            <div className="md:col-span-3 p-8 lg:p-10 text-white">
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/60 mb-2"><Send className="w-4 h-4" /> {t.reise.syncEyebrow}</span>
              <h3 className="font-[Playfair_Display] text-2xl lg:text-3xl font-bold mb-3">{t.reise.syncHeading}</h3>
              <p className="text-sm text-white/75 max-w-xl">{t.reise.syncText}</p>
            </div>
            <div className="md:col-span-2 p-8 lg:p-10 flex flex-col justify-center text-white border-t md:border-t-0 md:border-l border-white/10">
              <div className="flex items-center justify-between mb-3"><p className="text-xs uppercase tracking-wider text-white/50">{t.reise.summaryLabel}</p><span className="text-[10px] uppercase tracking-wider bg-white/10 text-white/70 rounded-full px-2 py-0.5">{t.reise.sampleTag}</span></div>
              <div className="rounded-xl bg-white/5 border border-white/10 p-4 text-sm space-y-2">
                {t.reise.sumRows.map((r) => (
                  <div key={r.l} className="flex justify-between"><span className="text-white/70">{r.l}</span><span className="font-medium">{r.v}</span></div>
                ))}
                <div className="h-px bg-white/10 my-1" />
                <div className="flex justify-between font-semibold"><span>{t.reise.sumTotal}</span><span>461,80</span></div>
              </div>
              <div className="mt-4 w-full rounded-full bg-white/90 text-[var(--brand-ink)] font-semibold py-2.5 inline-flex items-center justify-center gap-2 cursor-default select-none" data-testid="reisestudio-sync-btn" aria-hidden="true">{t.reise.syncBtn} <ArrowRight className="w-4 h-4" /></div>
              <p className="mt-2 text-center text-[11px] text-white/50">{t.reise.previewNote}</p>
            </div>
          </div>

          <div className="mt-12">
            <div className="text-center mb-2"><span className="inline-block text-xs uppercase tracking-[0.2em] text-[var(--brand-primary)] font-semibold">{t.reise.pkgEyebrow}</span></div>
            <h3 className="font-[Playfair_Display] text-2xl font-bold text-center text-[var(--brand-ink)] mb-3">{t.reise.pkgHeading}</h3>
            <p className="text-center text-[var(--brand-muted)] mb-8 max-w-2xl mx-auto">{t.reise.pkgSub}</p>

            <div className="flex flex-wrap justify-center gap-3 mb-10">
              {t.reise.plans.map((p) => {
                const m = REISE_PLAN_META[p.key];
                return (
                  <div key={p.key} className={`flex items-baseline gap-2 rounded-full border px-5 py-2.5 ${m.highlight ? "bg-[var(--brand-primary)] border-[var(--brand-primary)] text-white" : "bg-white border-[var(--brand-border)] text-[var(--brand-ink)]"}`}>
                    <span className={`text-xs uppercase tracking-wider font-semibold ${m.highlight ? "text-white/80" : "text-[var(--brand-muted)]"}`}>{p.name}</span>
                    <span className="text-lg font-bold">{m.price} €</span>
                    <span className={`text-xs ${m.highlight ? "text-white/70" : "text-[var(--brand-muted)]"}`}>{p.per}</span>
                  </div>
                );
              })}
            </div>

            <div className="space-y-6">
              {t.reise.plans.map((p) => {
                const m = REISE_PLAN_META[p.key];
                const Icon = m.icon;
                return (
                  <div key={p.key} className={`relative rounded-2xl overflow-hidden border grid grid-cols-1 md:grid-cols-5 bg-white ${m.highlight ? "border-[var(--brand-primary)] shadow-xl" : "border-[var(--brand-border)]"}`} data-testid={`reise-plan-${p.key}`}>
                    {m.highlight && (<span className="absolute top-4 right-4 z-10 inline-flex items-center gap-1 bg-white text-[var(--brand-ink)] text-xs px-3 py-1 rounded-full shadow"><Star className="w-3.5 h-3.5 fill-[var(--brand-primary)] text-[var(--brand-primary)]" /> {t.reise.recommended}</span>)}
                    <div className="md:col-span-3 p-8 lg:p-10">
                      <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[var(--brand-primary)] font-semibold mb-2"><Icon className="w-4 h-4" /> {p.name}</span>
                      <h4 className="font-[Playfair_Display] text-2xl font-bold text-[var(--brand-ink)] mb-2">Business Travel Studio {p.name}</h4>
                      <p className="text-sm text-[var(--brand-muted)] mb-5">{p.tagline}</p>
                      <p className="text-xs font-semibold uppercase tracking-wider text-[var(--brand-muted)] mb-3">{p.featsTitle}</p>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                        {p.feats.map((ft) => (<li key={ft} className="flex items-start gap-2 text-sm text-[var(--brand-ink)]"><CheckCircle2 className="w-4 h-4 text-[var(--brand-primary)] mt-0.5 shrink-0" /> {ft}</li>))}
                      </ul>
                    </div>
                    <div className="md:col-span-2 p-8 lg:p-10 flex flex-col justify-center text-white" style={{ background: m.highlight ? "linear-gradient(160deg, #5B636C 0%, #3E454C 100%)" : "linear-gradient(160deg, #7A828B 0%, #565E66 100%)" }}>
                      <p className="text-xs uppercase tracking-wider text-white/70">{t.reise.monthlyRent}</p>
                      <div className="my-2"><span className="text-5xl font-bold">{m.price} €</span><span className="text-white/75">{p.per}</span></div>
                      <p className="text-xs text-white/70 mb-5">{t.reise.orYear(p.year)}</p>
                      <Button onClick={() => (REISE_CHECKOUT[p.key] ? window.open(REISE_CHECKOUT[p.key], "_blank", "noopener,noreferrer") : onLogin())} className="w-full rounded-full bg-white text-[var(--brand-ink)] hover:bg-white/90 font-semibold" data-testid={`reise-start-${p.key}`}>{REISE_CHECKOUT[p.key] ? t.reise.buyNow : t.reise.startNow}</Button>
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="text-center text-xs text-[var(--brand-muted)] mt-8 max-w-2xl mx-auto">{t.reise.servicesNote}</p>
          </div>
        </div>
      </section>

      <WorkforceSection onLogin={onLogin} lang={lang} />

      <ExpenseSection onLogin={onLogin} lang={lang} />

      <CommerceSection onLogin={onLogin} lang={lang} />

      {/* Plattformfunktionen */}
      <section className="px-6 lg:px-12 py-16" style={{ background: "var(--brand-border-light)" }} data-testid="landing-platform">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-2"><span className="inline-block text-xs uppercase tracking-[0.2em] text-[var(--brand-primary)] font-semibold">{t.platform.eyebrow}</span></div>
          <h2 className="font-[Playfair_Display] text-3xl font-bold text-center text-[var(--brand-ink)] mb-3">{t.platform.heading}</h2>
          <p className="text-center text-[var(--brand-muted)] mb-10 max-w-2xl mx-auto">{t.platform.sub}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.platform.features.map((f) => (
              <div key={f.title} className="p-5 rounded-xl border border-[var(--brand-border)] bg-white hover:shadow-md transition-shadow">
                <h3 className="font-semibold text-[var(--brand-ink)] mb-1">{f.title}</h3>
                <p className="text-sm text-[var(--brand-muted)]">{f.text}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-[var(--brand-primary)] font-medium mt-8">{t.platform.note}</p>
        </div>
      </section>


      <section className="px-6 lg:px-12 py-16 max-w-6xl mx-auto" data-testid="landing-buchung">
        <div className="rounded-2xl border border-[var(--brand-border)] bg-[var(--brand-border-light)] p-8 lg:p-10 mb-10">
          <h2 className="font-[Playfair_Display] text-2xl sm:text-3xl font-bold text-[var(--brand-ink)] mb-6 text-center">{t.buchung.heading}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {t.buchung.steps.map((s, i) => (
              <div key={s.t} className="flex gap-4">
                <span className="flex-shrink-0 w-9 h-9 rounded-full bg-[var(--brand-primary)] text-white font-bold flex items-center justify-center">{i + 1}</span>
                <div><h3 className="font-semibold text-[var(--brand-ink)] mb-1">{s.t}</h3><p className="text-sm text-[var(--brand-muted)]">{s.d}</p></div>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-[var(--brand-muted)] mt-6">{t.buchung.note}</p>
        </div>

        <div className="rounded-2xl overflow-hidden" data-testid="landing-combine" style={{ background: "linear-gradient(160deg, #2C3338 0%, #1B1F23 100%)" }}>
          <div className="p-8 lg:p-10 text-white">
            <h2 className="font-[Playfair_Display] text-2xl sm:text-3xl font-bold mb-3">{t.buchung.combineHeading}</h2>
            <p className="text-sm text-white/75 mb-6 max-w-3xl">{t.buchung.combineText}</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              {t.buchung.combineList.map((li) => (
                <li key={li} className="flex items-start gap-2 text-sm text-white/90"><Lock className="w-4 h-4 text-white/60 mt-0.5 shrink-0" /> {li}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="px-6 lg:px-12 py-16 bg-white" data-testid="landing-founder">
        <div className="max-w-3xl mx-auto text-center">
          <span className="inline-block text-xs uppercase tracking-[0.2em] text-[var(--brand-primary)] font-semibold mb-3">{t.founder.eyebrow}</span>
          <h2 className="font-[Playfair_Display] text-3xl font-bold text-[var(--brand-ink)] mb-4">{t.founder.heading}</h2>
          <p className="text-[var(--brand-muted)] mb-8 text-left sm:text-center leading-relaxed">{t.founder.text}</p>
          <div className="rounded-2xl overflow-hidden border border-[var(--brand-border)] shadow-xl bg-black" data-testid="founder-video-wrap">
            <video controls playsInline preload="metadata" poster="/founder-poster.jpg" className="w-full aspect-video block object-cover bg-black" data-testid="founder-story-video">
              <source src="/founder-story.mp4" type="video/mp4" />
              {t.steps.videoFallback} <a href="/founder-story.mp4" className="underline">{t.steps.videoDownload}</a>
            </video>
          </div>
          {t.founder.videoNote ? <p className="text-xs text-[var(--brand-muted)] mt-3">{t.founder.videoNote}</p> : null}
        </div>
      </section>

      <section className="px-6 lg:px-12 py-16 bg-[var(--brand-bg)]" data-testid="landing-testimonials">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-2"><span className="inline-block text-xs uppercase tracking-[0.2em] text-[var(--brand-primary)] font-semibold">{t.testimonials.eyebrow}</span></div>
          <h2 className="font-[Playfair_Display] text-3xl font-bold text-center text-[var(--brand-ink)] mb-10">{t.testimonials.heading}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {t.testimonials.items.map((item) => (
              <div key={item.name} className="rounded-2xl border border-[var(--brand-border)] bg-white p-6 flex flex-col">
                <p className="text-sm text-[var(--brand-muted)] leading-relaxed flex-1 italic">&ldquo;{item.text}&rdquo;</p>
                <div className="mt-4 pt-4 border-t border-[var(--brand-border)]">
                  <p className="font-semibold text-[var(--brand-ink)]">{item.name}</p>
                  <p className="text-xs text-[var(--brand-muted)]">{item.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FaqSection t={t.faq} />

      <section className="px-6 lg:px-12 py-16 text-center" style={{ background: "linear-gradient(160deg, #2C3338 0%, #1B1F23 100%)" }} data-testid="landing-final-cta">
        <h2 className="font-[Playfair_Display] text-3xl font-bold text-white mb-5">{t.finalCta.heading}</h2>
        <Button onClick={demoFrom} className="bg-white text-[var(--brand-ink)] hover:bg-white/90 rounded-full px-10 py-4 text-base font-semibold shadow-md" data-testid="landing-final-demo-btn">{t.finalCta.btn}</Button>
        <p className="text-sm text-white/60 mt-4">{t.finalCta.note}</p>
      </section>

      <footer className="px-6 lg:px-12 py-8 text-center text-xs text-[var(--brand-muted)]">
        <button onClick={onWhiteLabel} className="hover:text-[var(--brand-ink)] hover:underline" data-testid="footer-whitelabel-link">{t.footer.wlLink}</button>
        <span className="mx-2">·</span>
        {t.footer.copyright(new Date().getFullYear())}
        <div className="mt-3 text-[var(--brand-muted)]">
          <button onClick={onImpressum} className="hover:underline">{t.footer.impressum}</button>
          <span className="mx-2">·</span>
          <button onClick={onDatenschutz} className="hover:underline">{t.footer.datenschutz}</button>
          <span className="mx-2">·</span>
          <button onClick={onAgb} className="hover:underline">{t.footer.agb}</button>
        </div>
        <div className="mt-6 pt-5 border-t border-[var(--brand-border)]" data-testid="footer-payment-methods">
          <p className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[var(--brand-muted)]">{t.footer.paymentLabel}</p>
          <div className="mt-3 flex flex-wrap items-center justify-center gap-2 text-[11px] font-bold tracking-[0.08em] text-[var(--brand-ink)]">
            {t.footer.paymentMethods.map((method) => (
              <span key={method} className="border border-[var(--brand-border)] bg-white px-3 py-1.5">{method}</span>
            ))}
          </div>
          <p className="mt-3 text-[11px] text-[var(--brand-muted)]">{t.footer.walletsSoon}</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
